import { z } from "zod";
import { PublicIdSchema } from "./common";

export const NewsStatus = z.enum(["draft", "published"]);
export const NewsTargetRole = z.enum(["admin", "office_staff", "driver"]);
// Flex Messageのbubble JSON 30KB上限に対し、最悪6byteへエスケープされる文字でも余裕を残す。
export const NEWS_BODY_MAX_LENGTH = 4_000;

const NewsTargetRoles = z.array(NewsTargetRole).superRefine((roles, ctx) => {
  if (new Set(roles).size !== roles.length) {
    ctx.addIssue({
      code: "custom",
      message: "target_roles に同じロールを重複指定できません",
    });
  }
});

const RequiredNewsTargetRoles = NewsTargetRoles.min(1, "お知らせ先を1件以上指定してください");
const UniquePublicIds = z.array(PublicIdSchema).superRefine((ids, ctx) => {
  if (new Set(ids).size !== ids.length) {
    ctx.addIssue({ code: "custom", message: "recipient_public_ids に重複を指定できません" });
  }
});

export const NewsItem = z.object({
  id: PublicIdSchema,
  title: z.string(),
  body: z.string(),
  status: NewsStatus,
  target_roles: NewsTargetRoles,
  published_at: z.iso.datetime({ offset: true }).nullable(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

export const ListNewsInput = z.object({
  status: NewsStatus.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
  // 互換用。新規呼び出しは cursor を優先する。
  offset: z.coerce.number().int().min(0).default(0),
});

export const CreateNewsInput = z
  .object({
    title: z.string().min(1).max(200),
    body: z.string().min(1),
    status: NewsStatus.optional().default("draft"),
    target_roles: NewsTargetRoles.optional().default([]),
  })
  .superRefine((input, ctx) => {
    if (input.status === "published" && input.target_roles.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["target_roles"],
        message: "公開するお知らせにはお知らせ先を1件以上指定してください",
      });
    }
  });

export const UpdateNewsInput = z.object({
  publicId: PublicIdSchema,
  title: z.string().min(1).max(200).optional(),
  body: z.string().min(1).optional(),
  status: NewsStatus.optional(),
  target_roles: NewsTargetRoles.optional(),
});

export const PreviewNewsSendInput = z.object({ target_roles: RequiredNewsTargetRoles });

export const NewsSendRecipient = z.object({
  public_id: PublicIdSchema,
  name: z.string(),
  role: NewsTargetRole,
});

export const NewsSendExcludedCounts = z.object({
  line_unlinked: z.number().int().nonnegative(),
  line_notifications_disabled: z.number().int().nonnegative(),
  inactive: z.number().int().nonnegative(),
  deleted: z.number().int().nonnegative(),
});

export const PreviewNewsSendOutput = z.object({
  recipients: z.array(NewsSendRecipient),
  excluded_counts: NewsSendExcludedCounts,
  recipient_count: z.number().int().nonnegative(),
  excluded_count: z.number().int().nonnegative(),
  line_push_available: z.boolean(),
});

export const SendNewsInput = z.object({
  request_id: PublicIdSchema,
  title: z
    .string()
    .min(1)
    .max(200)
    .refine((value) => value.trim().length > 0, "タイトルを入力してください"),
  body: z
    .string()
    .min(1)
    .max(NEWS_BODY_MAX_LENGTH)
    .refine((value) => value.trim().length > 0, "メッセージ本文を入力してください"),
  target_roles: RequiredNewsTargetRoles,
  recipient_public_ids: UniquePublicIds,
});

export const SendNewsOutput = z.object({ data: NewsItem, retried: z.boolean() });

export type NewsItemDto = z.infer<typeof NewsItem>;
export type NewsStatusDto = z.infer<typeof NewsStatus>;
export type NewsTargetRoleDto = z.infer<typeof NewsTargetRole>;
export type ListNewsInputDto = z.infer<typeof ListNewsInput>;
export type CreateNewsInputDto = z.infer<typeof CreateNewsInput>;
export type UpdateNewsInputDto = z.infer<typeof UpdateNewsInput>;
export type PreviewNewsSendInputDto = z.infer<typeof PreviewNewsSendInput>;
export type PreviewNewsSendOutputDto = z.infer<typeof PreviewNewsSendOutput>;
export type SendNewsInputDto = z.infer<typeof SendNewsInput>;
export type SendNewsOutputDto = z.infer<typeof SendNewsOutput>;
